import { useCallback } from 'react';
import { useSelector, useStore } from 'react-redux';
import {
  ChatOverlay,
  applyDomAutomation,
  collectDomDataTestIds,
} from '@unianlx-support/ua-ui-lib';

import { FROM_PAGE, APP_NAME } from '@/constants/app';
import { testIdBelongsToFromPage } from '@/utils/scorecardKbPageMatch';

const MODULE_ID = process.env.NEXT_PUBLIC_MODULE_ID || APP_NAME;
const HTTP_CHAT_URL = process.env.NEXT_PUBLIC_CHAT_API_URL || '';
const WS_CHAT_URL = process.env.NEXT_PUBLIC_CHAT_WS_URL || '';
const USE_WEB_SOCKET = process.env.NEXT_PUBLIC_CHAT_WS === 'true';

const DEFAULT_SUGGESTIONS = [
  'Explain how insurance allocation affects crash protection and opportunity cost',
  'Walk me through setting the year range and regime selectors on this screen',
  'Compare single-asset vs multi-asset scorecard results for my current inputs',
  'What do the percentile path charts mean for my selected assets?',
  'How should I interpret regime years versus reshuffled historical returns?',
];

const INPUT_KEYS = [
  'mode',
  'fromYear',
  'toYear',
  'yearsList',
  'selectedType',
  'firstAsset',
  'firstAssetName',
  'secondAsset',
  'secondAssetName',
  'insuranceSliderValue',
  'lock',
  'regimeCount',
  'regimeSelectors',
  'alloc',
  'percentileDropdown',
  'assets',
  'activeAsset',
  'secondAssets',
  'secondAssetNames',
  'insuranceSliderValues',
  'allocByAsset',
  'pin',
  'allocLock',
  'percentile1Dropdown',
  'percentile2Dropdown',
];

function buildPortfolioInputSnapshot(portfolio) {
  if (!portfolio || typeof portfolio !== 'object') return null;
  const snapshot = {};
  INPUT_KEYS.forEach((key) => {
    if (portfolio[key] !== undefined) {
      snapshot[key] = portfolio[key];
    }
  });
  return Object.keys(snapshot).length ? snapshot : null;
}

export default function ChatOverlayWrapper({
  user: userProp = null,
  suggestions = DEFAULT_SUGGESTIONS,
}) {
  const store = useStore();
  const userFromStore = useSelector((state) => state.userDetails.user);
  const user = userProp ?? userFromStore;
  const portfolio = useSelector((state) => state.portfolio);
  const fromAppPayload =
    portfolio?.requestPayload ?? buildPortfolioInputSnapshot(portfolio);

  const buildChatContext = useCallback(async () => {
    return {
      scorecardConfig: {
        fromPage: FROM_PAGE.MAIN,
        fromPageConfig: collectDomDataTestIds(
          FROM_PAGE.MAIN,
          undefined,
          (testId, fromPage) => testIdBelongsToFromPage(testId, fromPage)
        ),
        fromAppPayload,
      },
    };
  }, [fromAppPayload]);

  const onRunAutomation = useCallback(
    async (steps, { onStepProgress } = {}) => {
      await applyDomAutomation(steps, { mode: 'guided', onStepProgress, store });
    },
    [store]
  );

  return (
    <ChatOverlay
      user={user}
      moduleId={MODULE_ID}
      suggestions={suggestions}
      httpChatUrl={HTTP_CHAT_URL}
      wsChatUrl={WS_CHAT_URL}
      useWebSocket={USE_WEB_SOCKET}
      buildChatContext={buildChatContext}
      onRunAutomation={onRunAutomation}
      bootstrapOnOpen
      allowCsvAttach
    />
  );
}
